import { useMemo } from 'react';
import { View, Text, Pressable, Image } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import { KnockoutBracket } from '../../knockout/KnockoutBracket';
import type { KnockoutBracketModel } from '../../../src/knockout/types/knockoutBracket.types';
import {
  TOURNAMENT_BRACKET_EMPTY_TITLE,
  TOURNAMENT_BRACKET_EMPTY_SUBTITLE,
  TOURNAMENT_BRACKET_ERROR_TITLE,
  TOURNAMENT_BRACKET_RETRY,
  TOURNAMENT_BRACKET_TEST_IDS,
} from './TournamentBracket.constants';
import { getTournamentBracketStyles } from './TournamentBracket.styles';

export interface TournamentBracketViewProps {
  model: KnockoutBracketModel;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/**
 * The knockout bracket (specs/champions-league, Story 6).
 *
 * **Revised 2026-09-05 (AC-6.6).** AC-6.1 said "a generic bracket renders"; what shipped was a
 * vertical list of pick cards grouped by round — no tree, no route to the final, and a
 * two-legged tie drawn as two unrelated predictions. This now renders the SAME mirrored tree
 * the World Cup uses, from a normalized model, so both surfaces share one look.
 *
 * A two-legged tie is one box carrying the aggregate, with the legs beneath it (AC-6.7). The
 * tree is read-only: knockout fixtures are picked in the Picks tab (AC-6.8).
 */
export function TournamentBracketView({
  model, isLoading, isError, onRetry,
}: TournamentBracketViewProps) {
  const theme = useTheme();
  const s = useMemo(() => getTournamentBracketStyles(theme), [theme]);

  if (isLoading) {
    return (
      <View style={s.screen} testID={TOURNAMENT_BRACKET_TEST_IDS.screen}>
        <View style={s.skeletonRow} />
        <View style={s.skeletonRow} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={s.screen} testID={TOURNAMENT_BRACKET_TEST_IDS.screen}>
        <View style={s.centerWrap} testID={TOURNAMENT_BRACKET_TEST_IDS.error}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{TOURNAMENT_BRACKET_ERROR_TITLE}</Text>
          <Pressable
            style={s.retryButton}
            onPress={onRetry}
            testID={TOURNAMENT_BRACKET_TEST_IDS.retry}
            accessibilityRole="button"
          >
            <Text style={s.retryLabel}>{TOURNAMENT_BRACKET_RETRY}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // Before the draw every round is an empty placeholder, so "has rounds" is not the same
  // question as "has anything to show" (AC-6.3).
  const hasAnyTie = model.rounds.some((round) => round.matches.length > 0);
  if (!hasAnyTie) {
    return (
      <View style={s.screen} testID={TOURNAMENT_BRACKET_TEST_IDS.screen}>
        <View style={s.centerWrap} testID={TOURNAMENT_BRACKET_TEST_IDS.empty}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{TOURNAMENT_BRACKET_EMPTY_TITLE}</Text>
          <Text style={s.stateSubtitle}>{TOURNAMENT_BRACKET_EMPTY_SUBTITLE}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={s.screen} testID={TOURNAMENT_BRACKET_TEST_IDS.screen}>
      <KnockoutBracket model={model} testID={TOURNAMENT_BRACKET_TEST_IDS.tree} />
    </View>
  );
}
